import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { useTheme } from 'ui';
import { Autocomplete, TextField as TextFieldMUI } from 'vendor/material';
import DefaultValueLoading from 'pages/Settings/Account/components/FieldMapper/components/DefaultValue/DefaultValueLoading';
type OptionType = { display: string; value: string }[];
type AutocompleteProps = ComponentProps<typeof Autocomplete>;

const PLACEHOLDER_TEXT = 'Select a value';

interface SingleSelectInputProps {
  defaultValue?: AutocompleteProps['defaultValue'];
  disabled?: AutocompleteProps['disabled'];
  onBlur: AutocompleteProps['onBlur'];
  onChange: AutocompleteProps['onChange'];
  dataId?: string;
  errorMessage?: string;
  isLoading?: boolean;
  name: string;
  options: OptionType;
  placeholder?: string;
  value: OptionType[number]['value'];
}

export default function SingleSelectInput({
  dataId,
  disabled,
  errorMessage,
  isLoading,
  name,
  onChange,
  onBlur,
  options,
  placeholder = PLACEHOLDER_TEXT,
  value,
}: SingleSelectInputProps) {
  const theme = useTheme();

  const selectedOption = useMemo(() => {
    if (!options || !value) return null;
    return options.find((option) => option.value === value);
  }, [value, options]);

  if (isLoading) return <DefaultValueLoading />;

  return (
    <Autocomplete
      disabled={disabled}
      id={name}
      key={value}
      options={options || []}
      value={selectedOption || ''}
      getOptionSelected={(
        option: typeof options[number],
        value: typeof options[number]['value'],
      ) => {
        return option.value === value;
      }}
      getOptionLabel={(option: typeof options[number]) => {
        return option.display || '';
      }}
      renderOption={(option: typeof options[number]) => {
        return (
          <span
            style={{
              listStyle: 'none',
              fontSize: theme.typography.pxToRem(14),
            }}
          >
            {option.display}
          </span>
        );
      }}
      renderInput={(params) => {
        return (
          <TextFieldMUI
            {...params}
            data-id={dataId}
            id={name}
            placeholder={placeholder}
            error={!!errorMessage}
            helperText={errorMessage}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              ...params?.InputProps,
              style: {
                fontSize: theme.typography.pxToRem(14),
                fontWeight: 600,
                height: '40px',
              },
            }}
          />
        );
      }}
      onBlur={onBlur}
      onChange={onChange}
      fullWidth
    />
  );
}
